import React, { useImperativeHandle, useRef } from "react";

import Konva from "konva";
import { Stage, Layer } from "react-konva";

import { VIEWPORT_BUFFER_PX } from "../../constants";
import { useScrubbing } from "../../hooks/useScrubbing";
import { TAnimatedStyle, TRenderableRow } from "../../types";
import type { AnnotationFocus } from "../../utils/focus";
import { AnimatedRow } from "./AnimatedRow";

type Props = {
  focus: AnnotationFocus | null;
  width: number;
  height: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  scrollTop: number;
  durationSeconds: number;
  maxScroll: number;
  setScrollLeft: (fn: (prev: number) => number) => void;
  onScrub: (t: number | null) => void;
  onSegmentClick: (t: number) => void;
  transitions: (
    render: (style: TAnimatedStyle, item: TRenderableRow | null) => React.ReactNode | null,
  ) => React.ReactNode;
};

export interface TracksCanvasHandle {
  /**
   * Imperatively shift the tracks Layer's vertical scroll. Lets the label-pane's
   * native `onScroll` paint the canvas the same frame as the scrollbar moves —
   * no React render in between, no one-frame lag between label pane and tracks.
   * The Zustand `state.scrollTop` is still updated by the caller so wheel-zoom
   * and other consumers see consistent state.
   */
  setScrollTop: (y: number) => void;
}

const arePropsEqual = (prev: Props, next: Props): boolean => {
  if (prev.width !== next.width || prev.height !== next.height) return false;
  if (prev.pixelsPerSecond !== next.pixelsPerSecond) return false;
  // Exact, like the ruler: a tolerance lets small trackpad steps accumulate and
  // leaves the bars up to a pixel off the ruler and playhead once scrolling stops.
  if (prev.scrollLeft !== next.scrollLeft) return false;
  // Vertical scroll also lands imperatively through `setScrollTop`.
  if (Math.abs(prev.scrollTop - next.scrollTop) > 1) return false;
  return (
    prev.focus === next.focus &&
    prev.durationSeconds === next.durationSeconds &&
    prev.maxScroll === next.maxScroll &&
    prev.setScrollLeft === next.setScrollLeft &&
    prev.onScrub === next.onScrub &&
    prev.onSegmentClick === next.onSegmentClick &&
    prev.transitions === next.transitions
  );
};

export const TracksCanvas = React.memo(
  React.forwardRef<TracksCanvasHandle, Props>(
    (
      {
        focus,
        width,
        height,
        pixelsPerSecond,
        scrollLeft,
        scrollTop,
        durationSeconds,
        maxScroll,
        setScrollLeft,
        onScrub,
        onSegmentClick,
        transitions,
      },
      ref,
    ) => {
      const { stageRef, onPointerDown } = useScrubbing({
        pixelsPerSecond,
        scrollLeft,
        durationSeconds,
        width,
        maxScroll,
        setScrollLeft,
        onScrub,
      });

      const layerRef = useRef<Konva.Layer>(null);

      useImperativeHandle(
        ref,
        () => ({
          setScrollTop: (y: number) => {
            const layer = layerRef.current;
            if (!layer) return;

            layer.y(-y);
            layer.clipY(y);
            layer.batchDraw();
          },
        }),
        [],
      );

      // Snap the culling window outward to whole buffers: rows then re-render
      // once per buffer of scrolling, not every scroll frame, and always keep at
      // least one buffer of off-screen bars on each side.
      const visibleStartPx = Math.floor((scrollLeft - VIEWPORT_BUFFER_PX) / VIEWPORT_BUFFER_PX) * VIEWPORT_BUFFER_PX;
      const visibleEndPx =
        Math.ceil((scrollLeft + width + VIEWPORT_BUFFER_PX) / VIEWPORT_BUFFER_PX) * VIEWPORT_BUFFER_PX;
      const timelineWidth = durationSeconds * pixelsPerSecond;

      if (!(width > 0) || !(height > 0)) return null;

      return (
        <Stage height={height} listening={true} ref={stageRef} width={width} onPointerDown={onPointerDown}>
          <Layer
            clip={{ x: scrollLeft, y: scrollTop, width: width, height: height }}
            listening={true}
            ref={layerRef}
            x={-scrollLeft}
            y={-scrollTop}
          >
            {transitions((style: TAnimatedStyle, row: TRenderableRow | null) => {
              if (!row) return null;
              return (
                <AnimatedRow
                  durationSeconds={durationSeconds}
                  focus={focus}
                  pixelsPerSecond={pixelsPerSecond}
                  row={row}
                  style={style}
                  timelineWidth={timelineWidth}
                  visibleEndPx={visibleEndPx}
                  visibleStartPx={visibleStartPx}
                  onSegmentClick={onSegmentClick}
                />
              );
            })}
          </Layer>
        </Stage>
      );
    },
  ),
  arePropsEqual,
);

TracksCanvas.displayName = "TracksCanvas";
